import { entitySegment, modulePath } from './modulePaths';

/** Рядок запиту з непорожніх параметрів у заданому порядку (`?a=1&b=2`; без параметрів — порожній рядок). */
const query = (params: Record<string, string | undefined>): string => {
  const parts = Object.entries(params).flatMap(([k, v]) => (v ? [`${k}=${encodeURIComponent(v)}`] : []));
  return parts.length ? `?${parts.join('&')}` : '';
};

/** Параметр адреси кореня модуля, що позначає повернення до модуля, а не нове відкриття (routes.moduleReturn). */
export const MODULE_RETURN = 'return';

/**
 * Будівники маршрутів (06-Frontend §5, ADR-028). Усі посилання у застосунку формуються лише тут; basename (база збірки, типово `/app`)
 * додає router. Адреса не містить робочого простору (він — у стані сесії вкладки): модуль — `/<група>/<модуль>` (modulePaths), далі
 * сутність, запис (GUID) і вкладка або сторінка модуля, `settings`, `design`.
 */
export const routes = {
  root: () => '/',
  login: () => '/login',
  /** Головна поточного робочого простору. */
  home: () => '/',
  inbox: () => '/inbox',
  search: (q?: string) => `/search${q ? `?q=${encodeURIComponent(q)}` : ''}`,
  modules: () => '/modules',
  module: (module: string) => modulePath(module),
  /**
   * Повернення до модуля (прототип v4 page:'module' без openModule — крихта модуля, «← Модуль» і «Відкрити модуль» сторінок налаштувань):
   * остання сторінка модуля в тому самому стані; розгорнуті «Налаштування» й «Проєктування» лівого меню лишаються (ModulePage, ModuleMenu).
   */
  moduleReturn: (module: string) => `${modulePath(module)}?${MODULE_RETURN}=1`,
  /** Сторінка модуля: overview | advisory | analytics | help | ключ аналітичного подання (insight). */
  modulePage: (module: string, page: string) => `${modulePath(module)}/${page}`,
  /**
   * Налаштування модуля: fields | form | records | statuses | types; `focus` — тип запису, підсвічений на «Записах і формах» (прототип v4
   * cmFocus: спроєктована таблиця чи довідник з лівого меню); `type` — дочірня таблиця, налаштування якої відкрито (окремий модуль
   * прототипу: «Видатки» пакета «Фінанси депутата»).
   */
  moduleSettings: (module: string, item?: string, focus?: string, type?: string) =>
    `${modulePath(module)}/settings${item ? `/${item}` : ''}${query({ focus, type })}`,
  /**
   * «Налаштування» (системний модуль; прототип v4 cust-*): розділ і пункт розділу — ті самі адреси, що й appRoutes.customize застосунку,
   * для посилань сторінок платформи (повернення «← Форми» / «← Модулі», «Поля» → «Конструктор модулів» типу записів).
   */
  customize: (section?: string, item?: string) => `/customize${section ? `/${section}` : ''}${item ? `/${encodeURIComponent(item)}` : ''}`,
  /** Редактор форми з «Налаштування › Форми» (прототип v4 cust-form без inMod): модуль і, для неосновного типу записів, `?type=`. */
  customizeForm: (module: string, type?: string) => `/customize/forms/${encodeURIComponent(module)}${query({ type })}`,
  /** «Записи й форми» з «Налаштування › Встановлені модулі» (прототип v4 cust-module без inMod): модуль і підсвічений тип `?focus=`. */
  customizeModule: (module: string, focus?: string) => `/customize/modules/${encodeURIComponent(module)}${query({ focus })}`,
  /** Проєктування модуля: features | motivation | useCases | bi. */
  moduleDesign: (module: string, item?: string) => `${modulePath(module)}/design${item ? `/${item}` : ''}`,
  /** Список типу: подання, пункт меню (`?item=`) і режим власного подання (`?mode=` — режими бібліотеки бази знань). */
  list: (module: string, rt: string, view?: string, menuItem?: string, mode?: string) =>
    `${modulePath(module)}/${entitySegment(module, rt)}${view ? `/${view}` : ''}${query({ item: menuItem, mode })}`,
  /** Запис (GUID або `new` — нова повна форма) і вкладка. */
  record: (module: string, rt: string, id: string, tab?: string) => `${modulePath(module)}/${entitySegment(module, rt)}/${id}${tab ? `/${tab}` : ''}`,
  profile: () => '/profile',
};
